// File: oneportal-shell/src/pages/ManageLocations/ManageLocations.tsx

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  APIProvider,
  InfoWindow,
  Map,
  useMap,
} from "@vis.gl/react-google-maps";
import {
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  HEALTH_MARKER_COLORS,
  HEALTH_ORDER,
  MAP_CONTAINER_STYLE,
  MONOCHROME_MAP_ID,
  RIGHT_BOTTOM_CONTROL_POSITION,
} from "./ManageLocations.constants";
import { fetchLocations, type ManagedLocation } from "./manageLocationsService";
import { EditLocationModal } from "./EditLocationModal";
import styles from "./ManageLocations.module.css";

type SortMode = "alpha" | "health";

function hasCoordinates(
  location: ManagedLocation,
): location is ManagedLocation & { geoCoordinate: { latitude: number; longitude: number } } {
  return (
    Number.isFinite(location.geoCoordinate?.latitude) &&
    Number.isFinite(location.geoCoordinate?.longitude)
  );
}

function formatAddress(loc: ManagedLocation): string {
  const { addressLine1, city, stateProvinceRegionCode, zipPostalCode } = loc.address;
  const cityState = [city, stateProvinceRegionCode].filter(Boolean).join(", ");
  return [addressLine1, [cityState, zipPostalCode].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
}

function getLocationInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  return words.map((word) => word[0]).join("").toUpperCase().substring(0, 2);
}

function MapBounds({ locations }: { locations: ManagedLocation[] }) {
  const map = useMap();

  useEffect(() => {
    const maps = window.google?.maps;
    if (!map || !maps || locations.length === 0) return;

    const bounds = new maps.LatLngBounds();
    locations.forEach((location) => {
      if (hasCoordinates(location)) {
        bounds.extend({
          lat: location.geoCoordinate.latitude,
          lng: location.geoCoordinate.longitude,
        });
      }
    });
    map.fitBounds(bounds);
  }, [locations, map]);

  return null;
}

function LocationMarker({
  location,
  onSelect,
  onDeselect,
}: {
  location: ManagedLocation & { geoCoordinate: { latitude: number; longitude: number } };
  onSelect: (location: ManagedLocation) => void;
  onDeselect: (location: ManagedLocation | null) => void;
}) {
  const map = useMap();

  useEffect(() => {
    if (!map || !window.google?.maps?.marker?.AdvancedMarkerElement) return;

    const markerContent = document.createElement("div");
    markerContent.style.background = HEALTH_MARKER_COLORS[location.healthStatus] ?? "#334d99";
    markerContent.style.border = "2px solid #ffffff";
    markerContent.style.borderRadius = "50%";
    markerContent.style.boxSizing = "border-box";
    markerContent.style.color = "#ffffff";
    markerContent.style.fontSize = "14px";
    markerContent.style.fontWeight = "600";
    markerContent.style.height = "32px";
    markerContent.style.lineHeight = "28px";
    markerContent.style.textAlign = "center";
    markerContent.style.width = "32px";
    markerContent.textContent = getLocationInitials(location.name);

    const marker = new window.google.maps.marker.AdvancedMarkerElement({
      content: markerContent,
      position: {
        lat: location.geoCoordinate.latitude,
        lng: location.geoCoordinate.longitude,
      },
      title: location.name,
      map,
    });
    const handleMouseEnter = () => onSelect(location);
    const handleMouseLeave = () => onDeselect(null);
    markerContent.addEventListener("mouseenter", handleMouseEnter);
    markerContent.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      markerContent.removeEventListener("mouseenter", handleMouseEnter);
      markerContent.removeEventListener("mouseleave", handleMouseLeave);
      marker.map = null;
    };
  }, [location, map, onDeselect, onSelect]);

  return null;
}

const ManageLocations: React.FC<{ api?: unknown }> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  const [locations, setLocations] = useState<ManagedLocation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sortMode, setSortMode] = useState<SortMode>("alpha");
  const [editing, setEditing] = useState<ManagedLocation | null>(null);
  const [selectedMapLocation, setSelectedMapLocation] = useState<ManagedLocation | null>(null);

  const searchMirrorRef = useRef<HTMLSpanElement>(null);
  const [searchWidth, setSearchWidth] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    if (!search) {
      setSearchWidth(undefined);
      return;
    }
    setSearchWidth((searchMirrorRef.current?.offsetWidth ?? 0) + 6);
  }, [search]);

  async function loadLocations() {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await fetchLocations();
      setLocations(data);
    } catch {
      setLoadError(t("manageLocations.loadError"));
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleLocations = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term
      ? locations.filter(
        (loc) =>
          loc.name.toLowerCase().includes(term) ||
          formatAddress(loc).toLowerCase().includes(term),
      )
      : locations;

    const sorted = [...filtered];
    if (sortMode === "alpha") {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    } else {
      sorted.sort(
        (a, b) =>
          (HEALTH_ORDER[a.healthStatus] ?? 3) - (HEALTH_ORDER[b.healthStatus] ?? 3) ||
          a.name.localeCompare(b.name),
      );
    }
    return sorted;
  }, [locations, search, sortMode]);

  const mapLocations = useMemo(() => locations.filter(hasCoordinates), [locations]);

  function devicesLabel(loc: ManagedLocation): string {
    if (loc.offlineDevices <= 0) return t("manageLocations.card.allOnline");
    return t("manageLocations.card.offlineCount", { count: loc.offlineDevices });
  }

  function healthPercent(loc: ManagedLocation): number {
    if (loc.totalDevices <= 0) return 100;
    return Math.round((loc.healthyDevices / loc.totalDevices) * 100);
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.pageTitle}>{t("manageLocations.title")}</h1>
      </div>

      <div
        className={styles.mapPanel}
        role="region"
        aria-label={t("manageLocations.mapAlt")}
      >
        {!googleMapsApiKey ? (
          <p className={styles.stateMessage}>
            {t("manageLocations.mapUnavailable", "Map is unavailable.")}
          </p>
        ) : (
          <APIProvider apiKey={googleMapsApiKey} libraries={["marker"]}>
            <Map
              style={MAP_CONTAINER_STYLE}
              defaultZoom={DEFAULT_ZOOM}
              defaultCenter={DEFAULT_CENTER}
              mapId={import.meta.env.VITE_GOOGLE_MAP_ID || MONOCHROME_MAP_ID}
              mapTypeControl={false}
              clickableIcons={false}
              streetViewControl={false}
              fullscreenControl={true}
              zoomControl
              zoomControlOptions={{
                position: RIGHT_BOTTOM_CONTROL_POSITION,
              }}
              cameraControl={false}
              gestureHandling="greedy"
            >
              <MapBounds locations={mapLocations} />
              {mapLocations.map((location) => (
                <LocationMarker
                  key={location.id}
                  location={location}
                  onSelect={setSelectedMapLocation}
                  onDeselect={setSelectedMapLocation}
                />
              ))}
              {selectedMapLocation && hasCoordinates(selectedMapLocation) && (
                <InfoWindow
                  position={{
                    lat: selectedMapLocation.geoCoordinate.latitude,
                    lng: selectedMapLocation.geoCoordinate.longitude,
                  }}
                  headerDisabled
                  onCloseClick={() => setSelectedMapLocation(null)}
                >
                  <div className={styles.mapInfoWindow}>
                    <div className={styles.mapInfoHeader}>
                      <strong>{selectedMapLocation.name}</strong>
                      <button
                        type="button"
                        className={styles.mapInfoViewButton}
                        onClick={() => navigate(
                          `/dashboard/location?locationId=${encodeURIComponent(selectedMapLocation.id)}`,
                        )}
                      >
                        {t("manageLocations.card.view", "View")}
                      </button>
                    </div>
                    <p>{formatAddress(selectedMapLocation)}</p>
                    <div className={styles.mapInfoHealth}>
                      <span>{t("manageLocations.card.health")}</span>
                      <span className={styles[`health${selectedMapLocation.healthStatus}`] ?? ""}>
                        {selectedMapLocation.healthStatus}
                      </span>
                    </div>
                    <div className={styles.mapInfoStats}>
                      <span>{devicesLabel(selectedMapLocation)}</span>
                      <span>{selectedMapLocation.alertCount} {t("manageLocations.card.alertsLabel")}</span>
                    </div>
                  </div>
                </InfoWindow>
              )}
            </Map>
          </APIProvider>
        )}
      </div>

      <div className={styles.toolbar}>
        <div className={styles.searchWrapper}>
          <svg
            className={styles.searchIcon}
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M9 3a6 6 0 104.472 10.03l3.249 3.248a.75.75 0 101.06-1.06l-3.248-3.249A6 6 0 009 3zM4.5 9a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0z"
              fill="currentColor"
            />
          </svg>
          <input
            type="text"
            className={`${styles.searchInput} ${search ? styles.searchInputFilled : ""}`}
            style={search ? { width: searchWidth } : undefined}
            placeholder={t("manageLocations.searchPlaceholder")}
            aria-label={t("manageLocations.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className={styles.searchClear}
              aria-label={t("manageLocations.clearSearch")}
              onClick={() => setSearch("")}
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path
                  d="M15 5L5 15M5 5l10 10"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
          <span ref={searchMirrorRef} className={styles.searchMirror} aria-hidden="true">
            {search}
          </span>
        </div>

        <select
          className={styles.sortSelect}
          aria-label={t("manageLocations.sortLabel")}
          value={sortMode}
          onChange={(e) => setSortMode(e.target.value as SortMode)}
        >
          <option value="alpha">{t("manageLocations.sortAlpha")}</option>
          <option value="health">{t("manageLocations.sortHealth")}</option>
        </select>
      </div>

      {isLoading ? (
        <p className={styles.stateMessage}>{t("manageLocations.loading")}</p>
      ) : loadError ? (
        <p className={styles.stateMessage}>{loadError}</p>
      ) : visibleLocations.length === 0 ? (
        <div className={styles.emptyState}>
          <p className={styles.emptyTitle}>{t("manageLocations.emptyTitle")}</p>
          <p className={styles.emptySubtitle}>{t("manageLocations.emptySubtitle")}</p>
        </div>
      ) : (
        <div className={styles.grid}>
          {visibleLocations.map((loc) => (
            <div key={loc.id} className={styles.card}>
              <div className={styles.cardTop}>
                <div className={styles.cardTitleBlock}>
                  <p className={styles.cardName}>{loc.name}</p>
                  <p className={styles.cardAddress}>{formatAddress(loc)}</p>
                </div>
                <button
                  type="button"
                  className={styles.editButton}
                  aria-label={t("manageLocations.card.edit")}
                  onClick={() => setEditing(loc)}
                >
                  {t("manageLocations.card.edit")}
                </button>
              </div>

              <div className={styles.metric}>
                <div className={styles.healthHeader}>
                  <span className={styles.metricLabel}>{t("manageLocations.card.health")}</span>
                  <span
                    className={`${styles.healthText} ${styles[`health${loc.healthStatus}`] ?? ""}`}
                  >
                    {loc.healthStatus}
                  </span>
                </div>
                <div className={styles.healthBarTrack}>
                  <div
                    className={`${styles.healthBarFill} ${styles[`healthFill${loc.healthStatus}`] ?? ""}`}
                    style={{ width: `${healthPercent(loc)}%` }}
                  />
                </div>
              </div>

              <div className={styles.cardFooter}>
                <div className={styles.footerCol}>
                  <span className={styles.footerLabel}>{t("manageLocations.card.devices")}</span>
                  <span className={styles.footerValue}>{devicesLabel(loc)}</span>
                </div>
                <div className={styles.footerCol}>
                  <span className={styles.footerLabel}>
                    {t("manageLocations.card.alertsLabel")}
                  </span>
                  <span className={styles.footerValue}>{loc.alertCount}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EditLocationModal
          location={editing}
          onClose={() => setEditing(null)}
          onSuccess={() => {
            setEditing(null);
            loadLocations();
          }}
        />
      )}
    </div>
  );
};

export default ManageLocations;
